import React from 'react';
import { Video, Users } from 'lucide-react';
import './PlatformSelector.css';

interface PlatformOption {
  id: string;
  title: string;
  icon: React.ReactNode;
}

interface BYODOption {
  id: string;
  label: string;
}

interface PlatformSelectorProps {
  onSelect: (value: string, shouldAutoAdvance?: boolean) => void;
}

// Platform icons with brand colors
const TeamsIcon = () => (
  <div className="platform-icon teams-icon">
    <Users size={48} strokeWidth={1.5} />
  </div>
);

const ZoomIcon = () => (
  <div className="platform-icon zoom-icon">
    <Video size={48} strokeWidth={1.5} />
  </div>
);

const GoogleMeetIcon = () => (
  <div className="platform-icon google-meet-icon">
    <Video size={48} strokeWidth={1.5} />
  </div>
);

const platformOptions: PlatformOption[] = [
  {
    id: 'teams',
    title: 'Microsoft Teams',
    icon: <TeamsIcon />
  },
  {
    id: 'zoom',
    title: 'Zoom',
    icon: <ZoomIcon />
  },
  {
    id: 'meet',
    title: 'Google Meet',
    icon: <GoogleMeetIcon />
  }
];

const byodOptions: BYODOption[] = [
  { id: 'yes', label: 'Yes' },
  { id: 'no', label: 'No' },
  { id: 'not-sure', label: "I'm not sure" }
];

// Map platform types to quiz values
const mapPlatformToQuizValue = (platformType: string): string => {
  switch (platformType) {
    case 'teams':
      return 'Microsoft Teams';
    case 'zoom':
      return 'Zoom';
    case 'meet':
      return 'Bring Your Own Device (BYOD)';
    default:
      return 'Microsoft Teams';
  }
};

// Map BYOD response to quiz value
const mapBYODToQuizValue = (byodResponse: string): string => {
  switch (byodResponse) {
    case 'yes':
      return 'Bring Your Own Device (BYOD)';
    case 'no':
      return 'Microsoft Teams'; // Default fallback
    case 'not-sure':
      return 'Not Sure';
    default:
      return 'Not Sure';
  }
};

export const PlatformSelector: React.FC<PlatformSelectorProps> = ({ onSelect }) => {
  const [selectedPlatform, setSelectedPlatform] = React.useState<string | null>(null);
  const [selectedBYOD, setSelectedBYOD] = React.useState<string | null>(null);

  const handlePlatformSelect = (platformType: string) => {
    setSelectedPlatform(platformType);
    // Immediately register platform selection as the answer
    const quizValue = mapPlatformToQuizValue(platformType);
    
    // If BYOD has already been answered, auto-advance since both questions are complete
    if (selectedBYOD) {
      onSelect(quizValue, true);
    } else {
      // Don't auto-advance yet - user might want to answer BYOD too
      onSelect(quizValue, false);
    }
  };

  const handleBYODSelect = (byodResponse: string) => {
    setSelectedBYOD(byodResponse);
    
    if (selectedPlatform) {
      // Both questions answered - platform choice wins, auto-advance
      const quizValue = mapPlatformToQuizValue(selectedPlatform);
      onSelect(quizValue, true);
    } else if (byodResponse === 'yes') {
      // BYOD yes without platform selection - complete answer, auto-advance
      const quizValue = mapBYODToQuizValue(byodResponse);
      onSelect(quizValue, true);
    }
  };

  // Check if both questions have been answered
  const bothAnswered = selectedPlatform !== null && selectedBYOD !== null;
  const readyToProceed = bothAnswered || selectedBYOD === 'yes';
  const showCompletionHint = !readyToProceed && (selectedPlatform || selectedBYOD);

  return (
    <div className="platform-selector">
      <div className="platform-options-grid">
        {platformOptions.map((option) => (
          <button
            key={option.id}
            className={`platform-option-card ${selectedPlatform === option.id ? 'selected' : ''}`}
            onClick={() => handlePlatformSelect(option.id)}
            disabled={false}
          >
            {option.icon}
            <h3 className="platform-option-title">{option.title}</h3>
          </button>
        ))}
      </div>

      <div className="byod-section">
        <h4 className="byod-question">Will you bring your own device?</h4>
        <div className="byod-options">
          {byodOptions.map((option) => (
            <button
              key={option.id}
              className={`byod-option-button ${selectedBYOD === option.id ? 'selected' : ''}`}
              data-option-id={option.id}
              onClick={() => handleBYODSelect(option.id)}
              disabled={false}
            >
              {option.label}
            </button>
          ))}
        </div>
        
        <div
          className={`completion-hint ${showCompletionHint ? 'is-visible' : ''}`}
          aria-hidden={!showCompletionHint}
        >
          <p>Please answer both questions to continue</p>
        </div>
      </div>
    </div>
  );
};
